import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import { useTenant } from '../context/TenantContext'
import { useClient } from '../context/ClientContext'
import { useNPS } from '../hooks/useNPS'
import FeedbackModal from '../components/ui/FeedbackModal'
import NotFound from './NotFound'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../components/ui/accordion'
import fallbackFaqData from '../data/faq.json'
// import { el, se, te } from "date-fns/locale";
import { QuestionMark } from '../components/icons'
import apiService from '../services/api.service'
import { sanitizeHtml } from '../utils/sanitize'
import { isFaqEnabled } from '../utils/tenantFeatures'

const FAQ = () => {
  const { t, languageId } = useLanguage()
  const { tenant } = useTenant()
  const { tenanturlname } = useParams()
  const { client, isAuthenticated } = useClient()
  const { checkEligibility } = useNPS()

  const [faqList, setFaqlist] = useState([])
  const [subtitle, setSubtitle] = useState('')
  const [subtitle2, setSubtitle2] = useState('')
  const [loadingFaq, setLoadingFaq] = useState(true)
  const [faqData, setFaqData] = useState({})
  const [showNPS, setShowNPS] = useState(false)
  const [npsChecked, setNpsChecked] = useState(false)
  const faqEnteredAtRef = useRef(Date.now())

  const fallbackLanguage = t('languagecode') === 'ja' ? 'ja' : 'en'
  const fallbackNotice =
    fallbackFaqData?.[fallbackLanguage]?.importantNotice || {}

  const importantNotice = faqData?.importantNotice || fallbackNotice
  const noticeMessages = importantNotice?.messages || []
  const noticeTitle = importantNotice?.title || ''
  const noticeCtaLabel = importantNotice?.ctaLabel || ''
  const tenantUrl = tenant?.tenanturlname || tenanturlname
  const defaultInquiryPath = tenantUrl ? `/${tenantUrl}/inquiry` : '/inquiry'
  const rawNoticeCtaHref = importantNotice?.ctaHref || defaultInquiryPath
  const noticeCtaHref =
    rawNoticeCtaHref === '/inquiry' && tenantUrl
      ? defaultInquiryPath
      : rawNoticeCtaHref
  const noticeFooter = importantNotice?.footernotice || ''

  // Add CSS for links in FAQ content
  useEffect(() => {
    const style = document.createElement('style')
    style.innerHTML = `
      .faq-content a {
        color: #2563eb !important;
        text-decoration: underline !important;
        cursor: pointer !important;
      }
      .faq-content a:hover {
        color: #1d4ed8 !important;
      }
    `
    document.head.appendChild(style)
    return () => document.head.removeChild(style)
  }, [])

  // Check NPS eligibility after configurable dwell time on the FAQ page
  useEffect(() => {
    const npsConfig = tenant?.tenantmasterconfig?.nps_config
    if (!npsConfig?.enabled || !npsConfig?.triggers?.faq_visit?.enabled) return
    if (!isAuthenticated || !client?.clientId || npsChecked) return

    const triggerConfig = npsConfig.triggers.faq_visit
    const dwellAmount = triggerConfig?.min_dwell_amount ?? 10
    const dwellUnit = triggerConfig?.min_dwell_unit ?? 'seconds'
    const dwellMs = dwellUnit === 'milliseconds' ? dwellAmount : dwellAmount * 1000

    const timer = setTimeout(async () => {
      setNpsChecked(true)
      try {
        const result = await checkEligibility('faq_visit')
        if (result?.eligible) {
          setShowNPS(true)
        }
      } catch (error) {
        console.error('NPS eligibility check error:', error)
      }
    }, dwellMs)

    return () => clearTimeout(timer)
  }, [isAuthenticated, client?.clientId, npsChecked, checkEligibility, tenant])

  // When NPS modal closes, hide it without blocking router navigation.
  const handleNPSClose = useCallback(() => {
    setShowNPS(false)
  }, [])

  // Fetch FAQ data from API
  useEffect(() => {
    const fetchFAQData = async () => {
      if (!tenant?.tenantid || !isFaqEnabled(tenant)) {
        setLoadingFaq(false)
        return
      }

      try {
        const response = await apiService.get('/api/tenants/faq', {
          params: {
            tenantid: tenant?.tenantid,
            languageid: languageId,
          },
        })

        if (response && response.tenantfaq) {
          const faqContent = JSON.parse(response.tenantfaq)

          setFaqData(faqContent)
          setSubtitle(faqContent.subtitle || '')
          setSubtitle2(faqContent.subtitle2 || '')

          if (faqContent.categories) {
            setFaqlist(faqContent.categories)
          } else {
            setFaqlist([])
          }
        }
      } catch (error) {
        console.error('FAQ fetching error:', error)
        setFaqData({})
        setSubtitle('')
        setSubtitle2('')
        setFaqlist([])
      } finally {
        setLoadingFaq(false)
      }
    }
    fetchFAQData()
  }, [tenant, languageId])

  if (tenant && !isFaqEnabled(tenant)) {
    return <NotFound />
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 md:px-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col items-start mb-6 gap-1">
        <h1 className="text-base font-extrabold text-theme-secondary flex items-center gap-2">
          <QuestionMark className="h-5 w-5 text-theme-primary" />
          {tenant?.tenantfaqdisplay || t('faq.title') || 'FAQ'}
        </h1>
        <div className="flex justify-start w-full text-left whitespace-pre-line">{subtitle}</div>
        <div className="flex justify-start w-full text-left mt-2 whitespace-pre-line">{subtitle2}</div>
      </div>

      {/* FAQ Categories */}
      {(faqList || []).map((cat, catIdx) => (
        <div key={catIdx} className="mb-8">
          <h2 className="bg-blue-100 font-bold mb-1 p-15 rounded text-lg text-left">
            <span className="mr-3">{cat.icon} </span>
            {cat.title || cat.category}
          </h2>
          <Accordion
            type="single"
            collapsible
            className="faq-content max-h-[400px] overflow-y-auto overflow-x-hidden"
          >
            {(cat.items || []).map((item, idx) => (
              <AccordionItem
                value={`${catIdx}-${idx}`}
                key={`${catIdx}-${idx}`}
              >
                <AccordionTrigger>{item.question}</AccordionTrigger>
                <AccordionContent>
                  <div
                    className="whitespace-pre-line"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      ))}

      {(noticeTitle || noticeMessages.length > 0 || noticeCtaLabel) && (
        <div className="rounded border border-theme-primary/20 p-4 md:p-6 space-y-4 text-left">
          {noticeTitle && (
            <h3 className="text-lg font-bold text-theme-secondary whitespace-pre-line">
              {noticeTitle}
            </h3>
          )}

          <div className="space-y-3 text-theme-secondary">
            {noticeMessages.map((message, idx) => (
              <p
                key={`notice-${idx}`}
                className="whitespace-pre-line leading-relaxed"
              >
                {message}
              </p>
            ))}
          </div>

          {noticeCtaLabel && (
            <div>
              <a
                href={noticeCtaHref}
                className="text-theme-primary underline font-semibold"
              >
                {noticeCtaLabel}
              </a>

              {noticeFooter && (
                <div
                  className="text-red-500 mt-4 whitespace-pre-line [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(noticeFooter) }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {showNPS && (
        <FeedbackModal
          open={showNPS}
          onClose={() => setShowNPS(false)}
          triggerType="faq_visit"
          channel="IN_APP"
          npsData={{ timeSpentMs: Date.now() - faqEnteredAtRef.current }}
        />
      )}
    </div>
  )
}

export default FAQ
